import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MAX_PER_LINE, maxQuantity } from 'cart/cartLogic';
import { useCart } from 'context/CartContext';
import formatIDR from 'utils/formatIDR';
import Price from './Price';
import QuantityStepper from './QuantityStepper';
import ResponsiveImage from './ResponsiveImage';

/**
 * One cart line: thumbnail, name, option, price, quantity and remove.
 * `check` is what the server said about this line (cartValidation.js):
 * a line it cannot sell is marked and offers "Hapus" or "Sesuaikan jumlah";
 * a changed price is shown next to the old one. Without `check` (not
 * validated yet, or the demo catalog) the line shows what the cart remembers.
 */
export default function CartLine({ line, check, onNavigate, compact = false }) {
  const { t } = useTranslation();
  const { update, remove } = useCart();
  const href = `/produk/${line.slug}`;
  const gone = check && check.issues.includes('UNAVAILABLE');
  const soldOut = check && check.issues.includes('OUT_OF_STOCK');
  const tooMany = check && check.issues.includes('QUANTITY_EXCEEDS_STOCK');
  const serverMax = check && check.maxQuantity !== null ? Math.min(check.maxQuantity, MAX_PER_LINE) : null;
  const max = Math.max(1, serverMax !== null && !gone && !soldOut ? serverMax : maxQuantity(line));
  const price = check && check.priceChange ? check.priceChange : null;
  const unitPrice = check && check.unitPriceAmount !== null ? check.unitPriceAmount : line.priceAmount;
  const shownQty = tooMany && serverMax !== null ? Math.min(line.quantity, serverMax) : line.quantity;
  const blocked = gone || soldOut;
  const messageId = `line-msg-${line.variantId}`;

  return (
    <li className="flex gap-4 py-5" aria-describedby={check && check.blocking ? messageId : undefined}>
      <Link
        to={href}
        onClick={onNavigate}
        tabIndex={-1}
        aria-hidden="true"
        className={`relative shrink-0 overflow-hidden rounded-sm bg-surface-alt ${compact ? 'h-[100px] w-20' : 'h-[125px] w-[100px]'} ${blocked ? 'opacity-55' : ''}`}
      >
        <ResponsiveImage image={line.image} sizes="100px" alt="" className="h-full w-full object-cover" />
      </Link>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link to={href} onClick={onNavigate} className="font-medium leading-snug text-ink hover:underline">
              {line.name}
            </Link>
            {line.variantName && (
              <p className="mt-1 flex items-center gap-1.5 text-[13px] text-muted">
                {line.swatch && (
                  <span
                    aria-hidden="true"
                    className="h-3 w-3 rounded-full ring-1 ring-inset ring-black/10"
                    style={{ backgroundColor: line.swatch }}
                  />
                )}
                {line.optionName ? `${line.optionName}: ` : ''}
                {line.variantName}
              </p>
            )}
          </div>
          {!blocked && <Price amount={unitPrice * shownQty} className="shrink-0 text-right" size="sm" />}
        </div>

        {price && (
          <p className="mt-1.5 text-[13px] text-ink">
            {price.to > price.from
              ? t('cart.priceUp', { from: formatIDR(price.from), to: formatIDR(price.to) })
              : t('cart.priceDown', { from: formatIDR(price.from), to: formatIDR(price.to) })}
          </p>
        )}

        {blocked && (
          <div id={messageId} className="mt-2 text-[14px]">
            <p className="font-medium text-sale">{gone ? t('cart.unavailable') : t('cart.soldOut')}</p>
            <p className="mt-0.5 text-muted">{gone ? t('cart.unavailableHelp') : t('cart.soldOutHelp')}</p>
          </div>
        )}
        {tooMany && (
          <div id={messageId} className="mt-2 text-[14px]">
            <p className="font-medium text-sale">
              {t('cart.tooMany', { left: serverMax ?? 0, wanted: line.quantity })}
            </p>
            {serverMax > 0 && (
              <button
                type="button"
                onClick={() => update(line.variantId, serverMax)}
                className="mt-1 min-h-[40px] text-[14px] font-semibold underline underline-offset-4 hover:no-underline"
              >
                {t('cart.fixQuantity', { qty: serverMax })}
              </button>
            )}
          </div>
        )}

        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-3">
          {blocked ? (
            <span />
          ) : (
            <QuantityStepper
              size="sm"
              value={line.quantity}
              max={max}
              onChange={(q) => update(line.variantId, q)}
              label={t('cart.quantityFor', { name: line.name })}
            />
          )}
          <button
            type="button"
            onClick={() => remove(line.variantId)}
            className={`min-h-[40px] text-[13px] underline underline-offset-4 hover:text-ink ${blocked ? 'font-semibold text-ink' : 'text-muted'}`}
          >
            {t('cart.remove')}
            <span className="sr-only"> {line.name}</span>
          </button>
        </div>
        {!blocked && !tooMany && line.quantity >= max && (
          <p className="mt-2 text-[12px] text-muted">{t('cart.maxReached', { count: max })}</p>
        )}
      </div>
    </li>
  );
}
